import {
  ClockCircleOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Flex } from "antd";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClashNotificationPayload } from "shared/types";
import { textPx } from "shared/constants/zoom";
import { localizeServerDates } from "./notificationText";

// Person chip (prototype .chip / .chip.navy): instructors are navy, customers
// neutral. Mirrors the notification-prototype layout the product owner sent.
const Chip = ({
  icon,
  label,
  navy = false,
}: {
  icon: React.ReactNode;
  label: string;
  navy?: boolean;
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: textPx(11),
      fontWeight: 500,
      background: navy ? colors.navyTint : colors.bgSoft,
      color: navy ? colors.navyDark : colors.ink2,
    }}
  >
    <span
      style={{
        display: "inline-flex",
        fontSize: textPx(11),
        color: navy ? colors.navyDark : colors.ink3,
      }}
    >
      {icon}
    </span>
    {label}
  </span>
);

// Rich clash-digest body: "Conflict on DATE", the involved people as chips, and
// each clashing session as a row with its time.
export const ClashNotificationContent = ({
  payload,
}: {
  payload: ClashNotificationPayload;
}) => (
  <div style={{ marginTop: 4 }}>
    <div style={{ fontSize: textPx(12), color: colors.ink2, lineHeight: 1.5 }}>
      {translate("notifications.clashing.one_day.start_content")}{" "}
      {/* payload.date is stored as Indonesian text ("Senin, 12 Okt 2026"). */}
      <b style={{ color: colors.ink }}>{localizeServerDates(payload.date)}</b>
    </div>
    {payload.groups.map((group, gi) => (
      <div key={gi} style={{ marginTop: 8 }}>
        {(group.instructors.length > 0 || group.customers.length > 0) && (
          <Flex wrap="wrap" gap={5}>
            {group.instructors.map((name, i) => (
              <Chip key={`i-${i}`} icon={<UserOutlined />} label={name} navy />
            ))}
            {group.customers.map((name, i) => (
              <Chip key={`c-${i}`} icon={<TeamOutlined />} label={name} />
            ))}
          </Flex>
        )}
        <Flex vertical gap={5} style={{ marginTop: 8 }}>
          {group.sessions.map((session, i) => (
            <Flex
              key={i}
              justify="space-between"
              align="center"
              gap={8}
              style={{
                padding: "7px 10px",
                borderRadius: 8,
                background: colors.bgSoft,
                fontSize: textPx(11.5),
              }}
            >
              {/* A long class name is cut to one line; the full name shows
                  on hover. */}
              <span
                title={session.name}
                style={{
                  fontWeight: 600,
                  color: colors.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {session.name}
              </span>
              <span
                style={{
                  color: colors.ink2,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                }}
              >
                <ClockCircleOutlined style={{ fontSize: textPx(11) }} /> {session.time}
              </span>
            </Flex>
          ))}
        </Flex>
      </div>
    ))}
  </div>
);
